/**
 * reportClipboard — turns the generated rating narratives into the two payloads
 * the clipboard carries, matching how QME - Tool copies its exam (see that
 * project's `examOutput.ts` / `PhysicalExamV3.handleCopy`):
 *
 *   text/html   rich text, Times New Roman, keeping the bold/underline the
 *               narratives already carry, so it pastes into Word formatted
 *   text/plain  the same content flattened, for editors that take only text
 *
 * The narratives are internally generated HTML fragments (they contain <b> and
 * <u> and use \n for line breaks). The old copy path selected a hidden textarea
 * and ran document.execCommand('copy'), which threw every tag away — it claimed
 * to copy "in Times New Roman" but delivered unstyled plain text.
 *
 * Pure module: no React, no DOM. Unit-tested in reportClipboard.test.ts.
 */

const FONT = "font-family:'Times New Roman',serif;";

/** Tags the narratives legitimately use for emphasis; everything else is escaped. */
const ALLOWED_TAGS = /^<\/?(b|u|i|strong|em|br)\s*\/?>$/i;

/**
 * Escape a narrative for HTML output while preserving its own emphasis tags.
 * The narratives are built in this codebase, not user input, but escaping
 * anything unrecognised keeps a stray "<" in an exam value from breaking the
 * markup (or smuggling an element into the clipboard).
 */
function escapePreservingEmphasis(s: string): string {
  return s.replace(/<[^>]*>|[&<>]/g, (tok) => {
    if (tok === '&') return '&amp;';
    if (tok === '<') return '&lt;';
    if (tok === '>') return '&gt;';
    return ALLOWED_TAGS.test(tok) ? tok : tok.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  });
}

/** Collapse runs of blank lines so the paste is single-spaced. */
const collapse = (s: string) => s.replace(/\n{2,}/g, '\n');

/** Strip every tag — the text/plain payload. */
export const stripTags = (s: string): string => s.replace(/<[^>]*>/g, '');

/**
 * The text/html payload: one Times New Roman paragraph per narrative, blank
 * paragraphs between them, newlines as <br> so the line structure survives.
 */
export function narrativesToHtml(narratives: string[]): string {
  if (!narratives.length) return '';
  return narratives
    .map((n) => {
      const body = escapePreservingEmphasis(collapse(n.trim())).replace(/\n/g, '<br>');
      return `<p style="${FONT}">${body}</p>`;
    })
    .join(`<p style="${FONT}">&nbsp;</p>`);
}

/** The text/plain payload. */
export function narrativesToText(narratives: string[]): string {
  return collapse(narratives.map((n) => stripTags(n)).join('\n'));
}

const CLIPBOARD_TIMEOUT_MS = 2000;

/**
 * Neither clipboard call is guaranteed to settle — if the browser is waiting on a
 * permission decision the promise can hang indefinitely, leaving the button with
 * no feedback forever. Bound each attempt so the caller always gets an answer and
 * can offer the manual fallback.
 */
function withTimeout(p: Promise<unknown>, ms: number): Promise<boolean> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(false), ms);
    p.then(() => { clearTimeout(timer); resolve(true); })
     .catch(() => { clearTimeout(timer); resolve(false); });
  });
}

/**
 * Put rich text on the clipboard the way QME - Tool does: a single ClipboardItem
 * carrying both flavours, falling back to plain text when the browser refuses or
 * stalls on the rich item. Returns false only if both attempts fail.
 */
export async function writeToClipboard(
  html: string,
  text: string,
  timeoutMs: number = CLIPBOARD_TIMEOUT_MS,
): Promise<boolean> {
  const plain = () =>
    navigator.clipboard?.writeText
      ? withTimeout(navigator.clipboard.writeText(text), timeoutMs)
      : Promise.resolve(false);

  try {
    if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
      const rich = await withTimeout(
        navigator.clipboard.write([
          new ClipboardItem({
            'text/html': new Blob([html], { type: 'text/html' }),
            'text/plain': new Blob([text], { type: 'text/plain' }),
          }),
        ]),
        timeoutMs,
      );
      if (rich) return true;
      // Rich write refused or stalled — fall through to plain text.
    }
    return await plain();
  } catch {
    try {
      return await plain();
    } catch {
      return false;
    }
  }
}

/** Copy the rating narratives (the Results Summary "Copy All" button). */
export function copyReportToClipboard(narratives: string[]): Promise<boolean> {
  return writeToClipboard(narrativesToHtml(narratives), narrativesToText(narratives));
}
